/* ─── statusConfig.ts ───────────────────────────────────────────
 * Unified status configuration for all entity states in Pixory AI.
 * Used by StatusBadge component across Admin, SA and Portal.
 * ──────────────────────────────────────────────────────────────*/

export type StatusConfig = {
  label: string;
  icon: string;
  /** Tailwind bg + text classes for the badge pill */
  badge: string;
};

export const STATUS_CONFIG: Record<string, StatusConfig> = {
  /* ── General entity ──────────────────────────────────────── */
  active:    { label: "Active",    icon: "ri-checkbox-circle-line",  badge: "bg-emerald-400/15 text-emerald-400" },
  inactive:  { label: "Inactive",  icon: "ri-close-circle-line",     badge: "bg-white/10 text-white/40" },
  suspended: { label: "Suspended", icon: "ri-lock-line",             badge: "bg-red-400/15 text-red-400" },
  archived:  { label: "Archived",  icon: "ri-archive-line",          badge: "bg-white/8 text-white/30" },
  trial:     { label: "Trial",     icon: "ri-time-line",             badge: "bg-amber-400/15 text-amber-400" },

  /* ── Approval / request ──────────────────────────────────── */
  pending:  { label: "Pending",  icon: "ri-time-line",             badge: "bg-amber-400/15 text-amber-400" },
  approved: { label: "Approved", icon: "ri-checkbox-circle-line",  badge: "bg-emerald-400/15 text-emerald-400" },
  rejected: { label: "Rejected", icon: "ri-close-circle-line",     badge: "bg-red-400/15 text-red-400" },

  /* ── Contract status ─────────────────────────────────────── */
  draft:     { label: "Draft",     icon: "ri-draft-line",            badge: "bg-white/10 text-white/50" },
  signed:    { label: "Signed",    icon: "ri-pen-nib-line",          badge: "bg-emerald-400/15 text-emerald-400" },
  completed: { label: "Completed", icon: "ri-checkbox-circle-fill",  badge: "bg-[#C8A96E]/15 text-[#C8A96E]" },
  cancelled: { label: "Cancelled", icon: "ri-close-circle-line",     badge: "bg-red-400/15 text-red-400" },

  /* ── Signature ───────────────────────────────────────────── */
  unsigned: { label: "Unsigned", icon: "ri-file-warning-line", badge: "bg-amber-400/15 text-amber-400" },

  /* ── Payment confirmation (payments table) ───────────────── */
  confirmed: { label: "Confirmed", icon: "ri-checkbox-circle-line", badge: "bg-emerald-400/15 text-emerald-400" },
  refunded:  { label: "Refunded",  icon: "ri-arrow-go-back-line",   badge: "bg-violet-400/15 text-violet-400" },

  /* ── Contract payment_status ─────────────────────────────── */
  unpaid:         { label: "Unpaid",        icon: "ri-time-line",             badge: "bg-red-400/15 text-red-400" },
  deposit_paid:   { label: "Deposit Paid",  icon: "ri-coin-line",             badge: "bg-sky-400/15 text-sky-400" },
  partially_paid: { label: "Partial",       icon: "ri-funds-box-line",        badge: "bg-amber-400/15 text-amber-400" },
  paid:           { label: "Paid",          icon: "ri-checkbox-circle-line",  badge: "bg-emerald-400/15 text-emerald-400" },

  /* ── Job status ──────────────────────────────────────────── */
  scheduled:      { label: "Scheduled",     icon: "ri-calendar-check-line",  badge: "bg-sky-400/15 text-sky-400" },
  in_progress:    { label: "In Progress",   icon: "ri-loader-4-line",         badge: "bg-amber-400/15 text-amber-400" },
  editing:        { label: "Editing",       icon: "ri-scissors-line",         badge: "bg-violet-400/15 text-violet-400" },
  waiting_client: { label: "Waiting Client",icon: "ri-user-voice-line",       badge: "bg-orange-400/15 text-orange-400" },
  delivered:      { label: "Delivered",     icon: "ri-send-plane-line",       badge: "bg-blue-400/15 text-blue-400" },

  /* ── Delivery status ─────────────────────────────────────── */
  not_started: { label: "Not Started", icon: "ri-hourglass-line",   badge: "bg-slate-50 text-slate-400" },
  prepared:    { label: "Prepared",    icon: "ri-archive-line",     badge: "bg-sky-50 text-sky-600" },
  expired:     { label: "Expired",     icon: "ri-time-line",        badge: "bg-red-50 text-red-500" },

  /* ── Content / publication ───────────────────────────────── */
  published: { label: "Published", icon: "ri-global-line",           badge: "bg-emerald-400/15 text-emerald-400" },
  live:      { label: "Live",      icon: "ri-broadcast-line",        badge: "bg-emerald-400/15 text-emerald-400" },

  /* ── Job step ────────────────────────────────────────────── */
  blocked: { label: "Blocked", icon: "ri-error-warning-line", badge: "bg-red-400/15 text-red-400" },

  /* ── Integration queue ───────────────────────────────────── */
  sent:       { label: "Sent",       icon: "ri-check-double-line",    badge: "bg-emerald-400/15 text-emerald-400" },
  failed:     { label: "Failed",     icon: "ri-close-circle-line",    badge: "bg-red-400/15 text-red-400" },
  processing: { label: "Processing", icon: "ri-loader-4-line",        badge: "bg-sky-400/15 text-sky-400" },
  open:       { label: "Open",       icon: "ri-chat-3-line",          badge: "bg-emerald-400/15 text-emerald-400" },
  closed:     { label: "Closed",     icon: "ri-check-line",           badge: "bg-white/10 text-white/40" },

  /* ── Lead status ─────────────────────────────────────────── */
  new:       { label: "New",       icon: "ri-sparkling-line",        badge: "bg-sky-400/15 text-sky-400" },
  contacted: { label: "Contacted", icon: "ri-phone-line",            badge: "bg-amber-400/15 text-amber-400" },
  booked:    { label: "Booked",    icon: "ri-calendar-check-line",   badge: "bg-emerald-400/15 text-emerald-400" },
  lost:      { label: "Lost",      icon: "ri-close-circle-line",     badge: "bg-red-400/15 text-red-400" },

  /* ── RSVP ────────────────────────────────────────────────── */
  yes:   { label: "Attending",     icon: "ri-check-line",     badge: "bg-emerald-400/15 text-emerald-400" },
  no:    { label: "Not Attending", icon: "ri-close-line",     badge: "bg-red-400/15 text-red-400" },
  maybe: { label: "Maybe",         icon: "ri-question-line",  badge: "bg-amber-400/15 text-amber-400" },

  /* ── Appointment-specific ──────────────────────────────────── */
  no_show: { label: "No Show", icon: "ri-user-unfollow-line", badge: "bg-orange-400/15 text-orange-500" },
};

/** Fallback for unknown status values */
export const FALLBACK_STATUS: StatusConfig = {
  label: "Unknown",
  icon:  "ri-question-line",
  badge: "bg-white/10 text-white/40",
};

/**
 * Returns StatusConfig for a given status string.
 * Normalises to lowercase + trims. Falls back to FALLBACK with the raw label.
 */
export function getStatusCfg(status: string | null | undefined): StatusConfig {
  if (!status) return FALLBACK_STATUS;
  const key = status.trim().toLowerCase();
  return STATUS_CONFIG[key] ?? { ...FALLBACK_STATUS, label: status };
}